import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Plus, Trash2, Search, TrendingUp, TrendingDown, 
  BarChart3, Loader2, X, DollarSign, Calendar as CalendarIcon, Info, Globe, LineChart, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, ReferenceLine } from "recharts";
import { format, subYears } from "date-fns";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";

const CURRENCIES = [
  { value: "USD", label: "USD ($)", symbol: "$" },
  { value: "EUR", label: "EUR (€)", symbol: "€" },
  { value: "CHF", label: "CHF", symbol: "CHF" },
];

// KPI Info Button component with popover - lazy loads definition on click
const KpiInfoButton = ({ definitionKey, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [definition, setDefinition] = useState(null);
  
  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open && !definition) {
      // Only fetch definition when popover opens for the first time
      const def = t(`kpi.definitions.${definitionKey}`, { returnObjects: true });
      if (def && typeof def === 'object') {
        setDefinition(def);
      }
    }
  };
  
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button 
          className="inline-flex items-center justify-center text-slate-400 hover:text-[#0F766E] transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 sm:w-80 p-3 sm:p-4 text-xs sm:text-sm" side="top" align="center">
        {definition ? (
          <div className="space-y-2">
            <h4 className="font-semibold text-slate-900 text-xs sm:text-sm">{definition.title}</h4>
            <p className="text-slate-600 text-[11px] sm:text-xs">{definition.description}</p>
            <div className="pt-1.5 space-y-0.5 text-[10px] sm:text-xs">
              <p className="text-green-600">✓ {definition.excellent}</p>
              <p className="text-blue-600">• {definition.good}</p>
              <p className="text-amber-600">• {definition.average}</p>
              <p className="text-red-600">✗ {definition.poor}</p>
            </div>
            {definition.note && (
              <p className="text-slate-500 text-[10px] sm:text-xs italic pt-1 border-t border-slate-100">{definition.note}</p>
            )}
          </div>
        ) : (
          <div className="text-slate-400 text-xs">Loading...</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const PortfolioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const TIME_RANGES = [
    { value: "6M", label: t('timeRange.6M') },
    { value: "1Y", label: t('timeRange.1Y') },
    { value: "2Y", label: t('timeRange.2Y') },
    { value: "5Y", label: t('timeRange.5Y') },
    { value: "10Y", label: t('timeRange.10Y') },
  ];
  
  const PERIOD_YEARS = { "6M": 0.5, "1Y": 1, "2Y": 2, "5Y": 5, "10Y": 10 };

  const calcAnnualizedReturn = (startVal, endVal, customStartDate, periodOverride) => {
    if (!startVal || startVal <= 0 || !endVal || endVal <= 0) return null;
    let years;
    if (customStartDate) {
      const msPerYear = 365.25 * 24 * 60 * 60 * 1000;
      years = (new Date() - new Date(customStartDate)) / msPerYear;
    } else {
      years = PERIOD_YEARS[periodOverride || timeRange] || 1;
    }
    if (years <= 0) return null;
    return (Math.pow(endVal / startVal, 1 / years) - 1) * 100;
  };
  
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [timeRange, setTimeRange] = useState("1Y");
  const [startDate, setStartDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [nameSearchQuery, setNameSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Add from list state
  const [showLookupList, setShowLookupList] = useState(false);
  const [lookupAssets, setLookupAssets] = useState([]);
  const [lookupCategories, setLookupCategories] = useState([]);
  const [lookupGeographies, setLookupGeographies] = useState([]);
  const [lookupCategoryFilter, setLookupCategoryFilter] = useState("");
  const [lookupGeographyFilter, setLookupGeographyFilter] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  
  // KPI Modal state
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiSymbol, setKpiSymbol] = useState(null);
  const [kpiQuantity, setKpiQuantity] = useState("");
  const [kpiQuantityUpdating, setKpiQuantityUpdating] = useState(false);

  // AI Asset Analysis state
  const [aiAssetOpen, setAiAssetOpen] = useState(false);
  const [aiAssetResult, setAiAssetResult] = useState(null);
  const [aiAssetLoading, setAiAssetLoading] = useState(false);

  // Asset Graph Modal state
  const [assetGraphOpen, setAssetGraphOpen] = useState(false);
  const [assetGraphData, setAssetGraphData] = useState(null);
  const [assetGraphLoading, setAssetGraphLoading] = useState(false);
  const [assetGraphSymbol, setAssetGraphSymbol] = useState(null);
  const [assetGraphName, setAssetGraphName] = useState(null);
  const [assetGraphTimeRange, setAssetGraphTimeRange] = useState(timeRange);
  const [assetGraphCurrency, setAssetGraphCurrency] = useState("USD");

  // Currency state
  const [homeCurrency, setHomeCurrency] = useState("USD");
  const [currentExchangeRates, setCurrentExchangeRates] = useState({ USD: 1, EUR: 0.92, CHF: 0.88 });
  const [startExchangeRates, setStartExchangeRates] = useState({ USD: 1, EUR: 0.92, CHF: 0.88 });
  const [currencyLoading, setCurrencyLoading] = useState(false);

  const fetchPortfolio = useCallback(async () => {
    try {
      const data = await api.getPortfolio(id);
      setPortfolio(data);
      setHomeCurrency(data.home_currency || "USD");
      // Load saved start date
      if (data.calc_start_date) {
        setStartDate(new Date(data.calc_start_date));
      }
    } catch (error) {
      toast.error("Failed to load portfolio");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchExchangeRates = useCallback(async (startDateStr = null) => {
    try {
      // Always fetch current rates
      const currentRates = await api.getExchangeRates();
      setCurrentExchangeRates(currentRates);
      
      // Fetch historical rates if start date provided
      if (startDateStr) {
        const historicalRates = await api.getExchangeRates(startDateStr);
        setStartExchangeRates(historicalRates);
      } else {
        setStartExchangeRates(currentRates);
      }
    } catch (error) {
      console.error("Failed to fetch exchange rates:", error);
    }
  }, []);

  const fetchPerformance = useCallback(async () => {
    if (!portfolio?.assets?.length) {
      setPerformance(null);
      return;
    }
    setPerformanceLoading(true);
    try {
      const data = await api.getPortfolioPerformance(id, timeRange, null);
      setPerformance(data);
      
      // Fetch exchange rates for both start and current dates
      await fetchExchangeRates(data.calc_start_date);
    } catch (error) {
      console.error("Failed to load performance:", error);
    } finally {
      setPerformanceLoading(false);
    }
  }, [id, portfolio?.assets?.length, timeRange]);

  useEffect(() => {
    fetchPortfolio();
    fetchExchangeRates();
  }, [fetchPortfolio, fetchExchangeRates]);

  useEffect(() => {
    if (portfolio) {
      fetchPerformance();
    }
  }, [portfolio, timeRange, startDate, fetchPerformance]);

  const handleCurrencyChange = async (newCurrency) => {
    setHomeCurrency(newCurrency);
    setCurrencyLoading(true);
    try {
      await api.updatePortfolio(id, { home_currency: newCurrency });
      toast.success(`Currency changed to ${newCurrency}`);
    } catch (error) {
      toast.error("Failed to save currency preference");
    } finally {
      setCurrencyLoading(false);
    }
  };

  // Convert value from asset currency to home currency using specific exchange rates
  const convertToHomeCurrency = (value, fromCurrency = "USD", useStartRates = false) => {
    if (!value || fromCurrency === homeCurrency) return value;
    
    const rates = useStartRates ? startExchangeRates : currentExchangeRates;
    
    // Convert from source currency to USD first (if not USD)
    let valueInUSD = value;
    if (fromCurrency !== "USD") {
      const fromRate = rates[fromCurrency];
      if (fromRate) {
        valueInUSD = value / fromRate;
      }
    }
    
    // Convert from USD to home currency
    const toRate = rates[homeCurrency];
    if (toRate && homeCurrency !== "USD") {
      return valueInUSD * toRate;
    }
    
    return valueInUSD;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() && !nameSearchQuery.trim()) return;
    setSearching(true);
    try {
      const results = await api.searchSymbols(
        searchQuery.trim() || null, 
        nameSearchQuery.trim() || null
      );
      setSearchResults(results);
    } catch (error) {
      toast.error(t('common.search') + " failed");
    } finally {
      setSearching(false);
    }
  };

  const handleAddAsset = async () => {
    if (!selectedAsset || !quantity) {
      toast.error(t('auth.fillAllFields'));
      return;
    }
    setSubmitting(true);
    try {
      await api.addAsset(id, {
        symbol: selectedAsset.symbol,
        name: selectedAsset.name,
        asset_type: selectedAsset.type || "Equity",
        quantity: parseFloat(quantity),
      });
      toast.success(t('portfolio.assetAdded'));
      setAddAssetOpen(false);
      setSelectedAsset(null);
      setQuantity("");
      setSearchQuery("");
      setNameSearchQuery("");
      setSearchResults([]);
      setShowLookupList(false);
      setLookupCategoryFilter("");
      setLookupGeographyFilter("");
      fetchPortfolio();
    } catch (error) {
      toast.error(t('errors.failedAddAsset'));
    } finally {
      setSubmitting(false);
    }
  };

  const fetchLookupAssets = async (assetClassFilter = "", geographyFilter = "") => {
    setLookupLoading(true);
    try {
      const [assets, assetClasses, geographies] = await Promise.all([
        api.getAssetLookupList(assetClassFilter || null, geographyFilter || null),
        api.getAssetLookupAssetClasses(),
        api.getAssetLookupGeographies()
      ]);
      setLookupAssets(assets);
      setLookupCategories(assetClasses);
      setLookupGeographies(geographies);
    } catch (error) {
      console.error("Failed to fetch lookup assets:", error);
      toast.error(t('errors.failedLoadData'));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleShowLookupList = () => {
    setShowLookupList(true);
    fetchLookupAssets();
  };

  const handleLookupCategoryChange = (value) => {
    const filter = value === "all" ? "" : value;
    setLookupCategoryFilter(filter);
    fetchLookupAssets(filter, lookupGeographyFilter);
  };

  const handleLookupGeographyChange = (value) => {
    const filter = value === "all" ? "" : value;
    setLookupGeographyFilter(filter);
    fetchLookupAssets(lookupCategoryFilter, filter);
  };

  const handleSelectLookupAsset = async (asset) => {
    // Fetch current price when asset is selected
    let currentPrice = null;
    try {
      const results = await api.searchSymbols(asset.symbol, null);
      if (results && results.length > 0) {
        currentPrice = results[0].current_price;
      }
    } catch (error) {
      console.error("Failed to fetch price:", error);
    }
    
    setSelectedAsset({
      symbol: asset.symbol,
      name: asset.name,
      type: "Equity",
      region: asset.currency,
      currency: asset.currency,
      current_price: currentPrice
    });
    setShowLookupList(false);
  };

  const handleRemoveAsset = async (symbol) => {
    try {
      await api.removeAsset(id, symbol);
      toast.success(t('portfolio.assetRemoved'));
      fetchPortfolio();
    } catch (error) {
      toast.error(t('errors.failedRemoveAsset'));
    }
  };

  const handleShowKPIs = async (symbol) => {
    setKpiSymbol(symbol);
    setKpiModalOpen(true);
    setKpiLoading(true);
    setKpiData(null);
    
    // Get the current quantity from the portfolio
    const asset = portfolio?.assets?.find(a => a.symbol === symbol);
    setKpiQuantity(asset?.quantity?.toString() || "");
    
    try {
      const data = await api.getKPIs(symbol);
      setKpiData(data);
    } catch (error) {
      toast.error(t('errors.failedLoadKPIs'));
      console.error("KPI fetch error:", error);
    } finally {
      setKpiLoading(false);
    }
  };

  const handleUpdateQuantity = async () => {
    if (!kpiSymbol || !kpiQuantity) return;
    
    setKpiQuantityUpdating(true);
    try {
      await api.updateAssetQuantity(id, kpiSymbol, parseFloat(kpiQuantity));
      toast.success(t('portfolio.quantityUpdated'));
      fetchPortfolio(); // Refresh portfolio data
    } catch (error) {
      toast.error(t('errors.failedUpdateQuantity'));
      console.error("Failed to update quantity:", error);
    } finally {
      setKpiQuantityUpdating(false);
    }
  };

  // AI Asset Analysis handler
  const handleAiAssetAnalysis = async () => {
    if (!kpiSymbol) return;
    
    setAiAssetOpen(true);
    setAiAssetLoading(true);
    setAiAssetResult(null);
    
    try {
      const result = await api.analyzeAsset(kpiSymbol, i18n.language);
      setAiAssetResult(result);
    } catch (error) {
      toast.error(t('kpi.aiViewFailed'));
      console.error("AI asset analysis error:", error);
    } finally {
      setAiAssetLoading(false);
    }
  };

  const handleShowAssetGraph = async (symbol, name, currency) => {
    setAssetGraphSymbol(symbol);
    setAssetGraphName(name);
    setAssetGraphCurrency(currency || "USD");
    setAssetGraphTimeRange(timeRange);
    setAssetGraphOpen(true);
    setAssetGraphLoading(true);
    setAssetGraphData(null);
    try {
      const prices = await api.getPrices(symbol, timeRange);
      setAssetGraphData(prices);
    } catch (error) {
      toast.error(t('errors.failedLoadPrices'));
      console.error("Asset graph fetch error:", error);
    } finally {
      setAssetGraphLoading(false);
    }
  };

  const handleAssetGraphTimeRangeChange = async (newTimeRange) => {
    setAssetGraphTimeRange(newTimeRange);
    if (assetGraphSymbol) {
      setAssetGraphLoading(true);
      try {
        const prices = await api.getPrices(assetGraphSymbol, newTimeRange);
        setAssetGraphData(prices);
      } catch (error) {
        toast.error(t('errors.failedLoadPrices'));
      } finally {
        setAssetGraphLoading(false);
      }
    }
  };

  const handleDateSelect = async (date) => {
    setStartDate(date);
    setCalendarOpen(false);
    // Save to backend
    try {
      await api.updatePortfolio(id, { calc_start_date: format(date, 'yyyy-MM-dd') });
    } catch (error) {
      console.error("Failed to save start date:", error);
    }
  };

  const clearStartDate = async () => {
    setStartDate(null);
    // Save to backend
    try {
      await api.updatePortfolio(id, { calc_start_date: null });
    } catch (error) {
      console.error("Failed to clear start date:", error);
    }
  };

  const getCurrencySymbol = () => {
    return CURRENCIES.find(c => c.value === homeCurrency)?.symbol || "$";
  };

  // Format number with apostrophe as thousand separator
  const fmtNum = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    const parts = Number(num).toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    return parts.join('.');
  };

  const formatCurrency = (value, sourceCurrency = "USD", useStartRates = false) => {
    const converted = convertToHomeCurrency(value, sourceCurrency, useStartRates);
    const symbol = getCurrencySymbol();
    if (symbol === "CHF") {
      return `CHF ${fmtNum(converted)}`;
    }
    return `${symbol}${fmtNum(converted)}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatPercentRaw = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `${value.toFixed(2)}%`;
  };

  const formatRatio = (value) => {
    if (value === null || value === undefined) return "N/A";
    return value.toFixed(2);
  };

  const formatLargeNumber = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (value >= 1e12) return `${fmtNum(value / 1e12)}T`;
    if (value >= 1e9) return `${fmtNum(value / 1e9)}B`;
    if (value >= 1e6) return `${fmtNum(value / 1e6)}M`;
    return fmtNum(value, 0);
  };

  // Calculate correct portfolio totals by converting each asset to home currency
  const calculatePortfolioTotals = () => {
    if (!performance?.asset_values) {
      return { currentTotal: 0, startTotal: 0 };
    }
    
    let currentTotal = 0;
    let startTotal = 0;
    
    Object.entries(performance.asset_values).forEach(([symbol, data]) => {
      const assetCurrency = data.currency || "USD";
      currentTotal += convertToHomeCurrency(data.current_value, assetCurrency, false);
      startTotal += convertToHomeCurrency(data.start_value, assetCurrency, true);
    });
    
    return { currentTotal, startTotal };
  };

  const portfolioTotals = calculatePortfolioTotals();

  // Backend now returns chart values already converted to home currency
  const chartData = performance?.dates?.map((date, idx) => ({
    date,
    value: performance.values[idx],
  })) || [];

  // Find the closest chart date on or after the selected start date for the reference line
  const startDateRefLine = startDate && chartData.length > 0
    ? chartData.find(d => d.date >= format(startDate, 'yyyy-MM-dd'))?.date
    : null;

  const getChartTicks = () => {
    if (!chartData.length) return [];
    const isMonthly = ["6M", "1Y"].includes(timeRange);
    const intervalMonths = isMonthly ? 1 : 3;
    const ticks = [];
    const start = new Date(chartData[0].date);
    const end = new Date(chartData[chartData.length - 1].date);
    let current = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    if (!isMonthly) {
      const m = current.getMonth();
      current.setMonth(m + (3 - m % 3) % 3);
    }
    while (current <= end) {
      const closest = chartData.reduce((prev, curr) =>
        Math.abs(new Date(curr.date) - current) < Math.abs(new Date(prev.date) - current) ? curr : prev
      );
      ticks.push(closest.date);
      current.setMonth(current.getMonth() + intervalMonths);
    }
    return [...new Set(ticks)];
  };

  const minDate = subYears(new Date(), 5);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F766E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 sm:py-6">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Button 
                variant="ghost" 
                size="icon"
                data-testid="back-btn"
                onClick={() => navigate("/")}
                className="hover:bg-slate-100 h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl md:text-2xl font-semibold tracking-tight text-slate-900 truncate" style={{ fontFamily: 'Manrope' }}>
                  {portfolio?.name}
                </h1>
                {portfolio?.description && (
                  <p className="text-slate-500 text-xs sm:text-sm truncate hidden sm:block">{portfolio.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
              {/* Currency Selector */}
              <div className="flex items-center gap-1 sm:gap-2">
                <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 hidden sm:block" />
                <Select value={homeCurrency} onValueChange={handleCurrencyChange} disabled={currencyLoading}>
                  <SelectTrigger className="w-[70px] sm:w-[100px] h-8 sm:h-9 text-xs sm:text-sm" data-testid="currency-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((currency) => (
                      <SelectItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                variant="outline"
                data-testid="performance-btn"
                onClick={() => navigate(`/portfolio/${id}/performance`)}
                className="border-slate-200 h-8 sm:h-10 px-2 sm:px-4 text-xs sm:text-sm hidden md:flex"
              >
                <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t('portfolio.performance')}</span>
              </Button>
              <Button 
                data-testid="add-asset-btn"
                onClick={() => setAddAssetOpen(true)}
                className="bg-[#0F766E] hover:bg-[#115E59] text-white rounded-full px-3 sm:px-6 h-8 sm:h-10 text-xs sm:text-sm"
              >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t('portfolio.addAsset')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Chart Section */}
          <div className="lg:col-span-2">
            <Card className="border-slate-200 shadow-none">
              <CardHeader className="flex flex-col gap-3 sm:gap-4 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div>
                    <CardTitle className="text-base sm:text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
                      {t('portfolio.totalValue')}
                    </CardTitle>
                    {performance && (
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2">
                        <span className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                          {formatCurrency(portfolioTotals.currentTotal, homeCurrency, false)}
                        </span>
                        {(() => {
                          const percentChange = portfolioTotals.startTotal > 0 
                            ? ((portfolioTotals.currentTotal - portfolioTotals.startTotal) / portfolioTotals.startTotal * 100) 
                            : 0;
                          const annualized = calcAnnualizedReturn(
                            portfolioTotals.startTotal, portfolioTotals.currentTotal, null
                          );
                          return (
                            <>
                            <span className={`flex items-center text-xs sm:text-sm font-medium ${
                              percentChange >= 0 ? 'text-emerald-600' : 'text-red-500'
                            }`}>
                              {percentChange >= 0 ? (
                                <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                              ) : (
                                <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                              )}
                              {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(2)}%
                            </span>
                            {annualized !== null && (
                              <span className={`flex items-center text-xs sm:text-sm font-medium ${
                                annualized >= 0 ? 'text-emerald-600' : 'text-red-500'
                              }`} data-testid="portfolio-annualized-return">
                                {t('portfolio.annualizedReturn')}: {annualized >= 0 ? '+' : ''}{annualized.toFixed(2)}% p.a.
                              </span>
                            )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {TIME_RANGES.map((range) => (
                      <button
                        key={range.value}
                        data-testid={`time-range-${range.value}`}
                        onClick={() => setTimeRange(range.value)}
                        className={`time-range-btn ${timeRange === range.value ? 'active' : ''}`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Start Date Picker */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-2 border-t border-slate-100">
                  <span className="text-xs sm:text-sm text-slate-500">{t('portfolio.calculateFrom')}:</span>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="select-calc-start-date"
                        className={`justify-start text-left font-normal text-xs sm:text-sm h-8 ${startDate ? 'text-slate-900' : 'text-slate-500'}`}
                      >
                        <CalendarIcon className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        {startDate ? format(startDate, 'MMM d, yyyy') : t('portfolio.startOfPeriod')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={handleDateSelect}
                        disabled={(date) => date > new Date() || date < minDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {startDate && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={clearStartDate}
                      className="text-slate-500 hover:text-slate-700 text-xs sm:text-sm h-8"
                    >
                      {t('portfolio.clearDate')}
                    </Button>
                  )}
                  {startDate && chartData.length > 0 && (() => {
                    const startDateStr = format(startDate, 'yyyy-MM-dd');
                    const startEntry = chartData.find(d => d.date >= startDateStr);
                    const endEntry = chartData[chartData.length - 1];
                    if (!startEntry || !endEntry || startEntry.value <= 0) return null;
                    const sdChange = ((endEntry.value - startEntry.value) / startEntry.value * 100);
                    const sdAnnualized = calcAnnualizedReturn(startEntry.value, endEntry.value, startDateStr);
                    return (
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 ml-1 pl-2 sm:pl-3 border-l border-slate-200" data-testid="start-date-stats">
                        <span className={`text-xs sm:text-sm font-medium ${sdChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {sdChange >= 0 ? '+' : ''}{sdChange.toFixed(2)}%
                        </span>
                        {sdAnnualized !== null && (
                          <span className={`text-xs sm:text-sm font-medium ${sdAnnualized >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {sdAnnualized >= 0 ? '+' : ''}{sdAnnualized.toFixed(2)}% p.a.
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                {performanceLoading ? (
                  <div className="h-48 sm:h-64 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin text-[#0F766E]" />
                  </div>
                ) : chartData.length > 0 ? (
                  <div className="h-48 sm:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2DD4BF" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#2DD4BF" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis 
                          dataKey="date" 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10, fill: '#64748B' }}
                          ticks={getChartTicks()}
                          tickFormatter={(value) => {
                            const d = new Date(value);
                            const mm = String(d.getMonth() + 1).padStart(2, '0');
                            const yy = String(d.getFullYear()).slice(-2);
                            return `${mm}-${yy}`;
                          }}
                        />
                        <YAxis 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10, fill: '#64748B' }}
                          tickFormatter={(value) => {
                            const sym = getCurrencySymbol();
                            const k = Math.round(value / 1000);
                            return `${sym}${String(k).replace(/\B(?=(\d{3})+(?!\d))/g, "'")}k`;
                          }}
                          width={50}
                          domain={['dataMin', 'dataMax']}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #E2E8F0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                            fontSize: '12px'
                          }}
                          formatter={(value) => [formatCurrency(value, homeCurrency), t('portfolio.totalValue')]}
                          labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { 
                            month: 'long', day: 'numeric', year: 'numeric' 
                          })}
                        />
                        <Area 
                          type="linear" 
                          dataKey="value" 
                          stroke="#0F766E" 
                          strokeWidth={2}
                          fill="url(#colorValue)" 
                        />
                        {startDateRefLine && (
                          <ReferenceLine 
                            x={startDateRefLine}
                            stroke="#6366F1"
                            strokeWidth={1.5}
                            strokeDasharray="4 4"
                            label={{
                              value: format(startDate, 'MM-dd'),
                              position: 'top',
                              fill: '#6366F1',
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-48 sm:h-64 flex flex-col items-center justify-center text-slate-400">
                    <BarChart3 className="w-10 h-10 sm:w-12 sm:h-12 mb-3 opacity-50" />
                    <p className="text-sm">{t('portfolio.noAssetsText')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stats */}
          <div className="space-y-3 sm:space-y-4">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-3 sm:pt-6 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-teal-50 rounded-lg sm:rounded-xl">
                    <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-[#0F766E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">{t('portfolio.totalValue')} ({homeCurrency})</p>
                    <p className="text-lg sm:text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                      {formatCurrency(portfolioTotals.currentTotal, homeCurrency, false)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-3 sm:pt-6 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  {(() => {
                    const percentChange = portfolioTotals.startTotal > 0 
                      ? ((portfolioTotals.currentTotal - portfolioTotals.startTotal) / portfolioTotals.startTotal * 100) 
                      : 0;
                    return (
                      <>
                        <div className="p-2 sm:p-3 bg-teal-50 rounded-lg sm:rounded-xl">
                          {percentChange >= 0 ? (
                            <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" strokeWidth={1.5} />
                          ) : (
                            <TrendingDown className="w-4 h-4 sm:w-6 sm:h-6 text-red-500" strokeWidth={1.5} />
                          )}
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-slate-500">
                            {t('portfolio.change')} {startDate ? `(${t('common.from')} ${format(startDate, 'MMM d')})` : `(${timeRange})`}
                          </p>
                          <p className={`text-lg sm:text-2xl font-semibold ${
                            percentChange >= 0 ? 'text-emerald-600' : 'text-red-500'
                          }`} style={{ fontFamily: 'Manrope' }}>
                            {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(2)}%
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
            {portfolioTotals.startTotal > 0 && (
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-3 sm:pt-6 sm:p-6">
                  <p className="text-xs sm:text-sm text-slate-500 mb-1 sm:mb-2">{t('portfolio.valueChange')} ({homeCurrency})</p>
                  {(() => {
                    const changeValue = portfolioTotals.currentTotal - portfolioTotals.startTotal;
                    return (
                      <>
                        <p className={`text-base sm:text-lg font-semibold ${changeValue >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {changeValue >= 0 ? '+' : ''}{formatCurrency(changeValue, homeCurrency, false)}
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-400 mt-1">
                          {t('common.from')} {formatCurrency(portfolioTotals.startTotal, homeCurrency, false)}
                        </p>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Assets List with Values */}
        <Card className="border-slate-200 shadow-none mt-4 sm:mt-6">
          <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
            <CardTitle className="text-base sm:text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              {t('portfolio.holdings')} ({portfolio?.assets?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {portfolio?.assets?.length === 0 ? (
              <div className="text-center py-8 sm:py-12 text-slate-400">
                <p className="text-sm">{t('portfolio.noAssets')}</p>
                <Button 
                  variant="link" 
                  className="text-[#0F766E] mt-2 text-sm"
                  onClick={() => setAddAssetOpen(true)}
                >
                  {t('portfolio.addAsset')}
                </Button>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {/* Header row - hidden on mobile */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100">
                  <div className="col-span-3">{t('portfolio.symbol')}</div>
                  <div className="col-span-1 text-right">{t('portfolio.quantity')}</div>
                  <div className="col-span-2 text-right">{t('portfolio.startValue')} ({homeCurrency})</div>
                  <div className="col-span-2 text-right">{t('portfolio.currentValue')} ({homeCurrency})</div>
                  <div className="col-span-2 text-right">{t('portfolio.change')}</div>
                  <div className="col-span-2 text-right">{t('portfolio.actions')}</div>
                </div>
                <AnimatePresence>
                  {portfolio?.assets?.map((asset, index) => {
                    const assetPerf = performance?.asset_values?.[asset.symbol];
                    const assetCurrency = assetPerf?.currency || "USD";
                    const startConverted = assetPerf ? convertToHomeCurrency(assetPerf.start_value, assetCurrency, true) : 0;
                    const currentConverted = assetPerf ? convertToHomeCurrency(assetPerf.current_value, assetCurrency, false) : 0;
                    const changeValue = currentConverted - startConverted;
                    const changePercent = startConverted > 0 ? ((currentConverted - startConverted) / startConverted * 100) : 0;
                    
                    return (
                      <motion.div
                        key={asset.symbol}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.05 }}
                        data-testid={`asset-${asset.symbol}`}
                        className="border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        {/* Mobile Layout - Compact 2 rows */}
                        <div className="md:hidden p-2.5">
                          {/* Row 1: Name/Symbol + Current Value + Actions */}
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-slate-900 text-sm truncate">{asset.name}</p>
                                <span className="text-[10px] text-slate-400">×{asset.quantity}</span>
                              </div>
                              <p className="text-[11px] text-slate-500">{asset.symbol}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-semibold text-slate-900 text-sm">
                                {assetPerf ? formatCurrency(currentConverted, homeCurrency, false) : '-'}
                              </p>
                              <p className={`text-[11px] font-medium ${changePercent >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%
                              </p>
                            </div>
                            <div className="flex gap-0 flex-shrink-0 ml-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`graph-asset-${asset.symbol}`}
                                onClick={() => handleShowAssetGraph(asset.symbol, asset.name, assetPerf?.currency)}
                                className="text-slate-400 hover:text-[#0F766E] h-7 w-7"
                              >
                                <LineChart className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`info-asset-${asset.symbol}`}
                                onClick={() => handleShowKPIs(asset.symbol)}
                                className="text-slate-400 hover:text-[#0F766E] h-7 w-7"
                              >
                                <Info className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`remove-asset-${asset.symbol}`}
                                onClick={() => handleRemoveAsset(asset.symbol)}
                                className="text-slate-400 hover:text-red-500 h-7 w-7"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Desktop Layout */}
                        <div className="hidden md:grid md:grid-cols-12 gap-4 items-center p-4">
                          {/* Asset Info */}
                          <div className="col-span-3">
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 text-base truncate">{asset.name}</p>
                              <p className="text-sm text-slate-500">{asset.symbol}</p>
                            </div>
                          </div>
                          
                          {/* Quantity */}
                          <div className="col-span-1 text-right">
                            <p className="font-medium text-base">{asset.quantity}</p>
                          </div>
                          
                          {/* Start Value */}
                          <div className="col-span-2 text-right">
                            <p className="font-medium text-slate-700 text-sm">
                              {assetPerf ? formatCurrency(assetPerf.start_value, assetCurrency, true) : '-'}
                            </p>
                            {assetPerf && (
                              <p className="text-xs text-slate-400">
                                @{formatCurrency(assetPerf.start_price, assetCurrency, true)}
                              </p>
                            )}
                          </div>
                          
                          {/* Current Value */}
                          <div className="col-span-2 text-right">
                            <p className="font-semibold text-slate-900 text-base">
                              {assetPerf ? formatCurrency(assetPerf.current_value, assetCurrency, false) : '-'}
                            </p>
                            {assetPerf && (
                              <p className="text-xs text-slate-400">
                                @{formatCurrency(assetPerf.current_price, assetCurrency, false)}
                              </p>
                            )}
                          </div>
                          
                          {/* Change */}
                          <div className="col-span-2 text-right">
                            {assetPerf ? (
                              <>
                                <p className={`font-semibold text-base ${changePercent >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%
                                </p>
                                <p className={`text-xs ${changeValue >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                                  {changeValue >= 0 ? '+' : ''}{formatCurrency(changeValue, homeCurrency, false)}
                                </p>
                              </>
                            ) : (
                              <p className="text-slate-400">-</p>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="col-span-2 flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`graph-asset-${asset.symbol}`}
                              onClick={() => handleShowAssetGraph(asset.symbol, asset.name, assetPerf?.currency)}
                              className="text-slate-400 hover:text-[#0F766E] h-9 w-9"
                              title="View Price Graph"
                            >
                              <LineChart className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`info-asset-${asset.symbol}`}
                              onClick={() => handleShowKPIs(asset.symbol)}
                              className="text-slate-400 hover:text-[#0F766E] h-9 w-9"
                              title="View KPIs"
                            >
                              <Info className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`remove-asset-${asset.symbol}`}
                              onClick={() => handleRemoveAsset(asset.symbol)}
                              className="text-slate-400 hover:text-red-500 h-9 w-9"
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Asset Dialog */}
      <Dialog open={addAssetOpen} onOpenChange={(open) => {
        setAddAssetOpen(open);
        if (!open) {
          setShowLookupList(false);
          setLookupCategoryFilter("");
        }
      }}>
        <DialogContent className="sm:max-w-lg mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="text-base sm:text-lg">{t('portfolio.addAsset')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            
            {/* Add from List Button */}
            {!showLookupList && (
              <Button 
                variant="outline"
                onClick={handleShowLookupList}
                className="w-full h-10 sm:h-12 border-dashed border-2 hover:border-[#0F766E] hover:text-[#0F766E]"
                data-testid="add-from-list-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {t('portfolio.addFromList')}
              </Button>
            )}

            {/* Lookup List View */}
            {showLookupList && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{t('portfolio.selectFromList')}</Label>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowLookupList(false)}
                    className="text-xs h-8"
                  >
                    {t('portfolio.backToSearch')}
                  </Button>
                </div>
                
                {/* Filters Row */}
                <div className="grid grid-cols-2 gap-2">
                  {/* Asset Class Filter */}
                  <Select value={lookupCategoryFilter || "all"} onValueChange={handleLookupCategoryChange}>
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue placeholder={t('assetLookup.filterByAssetClass')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('assetLookup.allAssetClasses')}</SelectItem>
                      {lookupCategories.map((ac) => (
                        <SelectItem key={ac.en} value={ac.en}>
                          {i18n.language?.startsWith('fr') ? (ac.fr || ac.en) : i18n.language?.startsWith('de') ? ac.de : ac.en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Geography Filter */}
                  <Select value={lookupGeographyFilter || "all"} onValueChange={handleLookupGeographyChange}>
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue placeholder={t('assetLookup.filterByGeography')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('assetLookup.allGeographies')}</SelectItem>
                      {lookupGeographies.map((geo) => (
                        <SelectItem key={geo.en} value={geo.en}>
                          {i18n.language?.startsWith('fr') ? (geo.fr || geo.en) : i18n.language?.startsWith('de') ? geo.de : geo.en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Lookup Asset List */}
                <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                  {lookupLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-[#0F766E]" />
                    </div>
                  ) : lookupAssets.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      {t('assetLookup.noAssets')}
                    </div>
                  ) : (
                    lookupAssets.map((asset) => (
                      <button
                        key={asset.id}
                        data-testid={`lookup-asset-${asset.symbol}`}
                        onClick={() => handleSelectLookupAsset(asset)}
                        className={`w-full text-left p-2.5 sm:p-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors ${
                          selectedAsset?.symbol === asset.symbol ? 'bg-teal-50' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 text-sm">{asset.name}</p>
                            <p className="text-xs text-slate-500">{asset.symbol}</p>
                            {/* Show additional classifications if available */}
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(asset.sector_en || asset.sector_de) && (
                                <span className="text-[9px] sm:text-[10px] text-slate-400">
                                  {i18n.language?.startsWith('fr') ? (asset.sector_fr || asset.sector_en) : i18n.language?.startsWith('de') ? asset.sector_de : asset.sector_en}
                                </span>
                              )}
                              {(asset.geography_en || asset.geography_de) && (
                                <span className="text-[9px] sm:text-[10px] text-slate-400">
                                  • {i18n.language?.startsWith('fr') ? (asset.geography_fr || asset.geography_en) : i18n.language?.startsWith('de') ? asset.geography_de : asset.geography_en}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-[10px] sm:text-xs bg-teal-100 text-teal-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                              {i18n.language?.startsWith('fr') ? (asset.asset_class_fr || asset.asset_class_en) : i18n.language?.startsWith('de') ? asset.asset_class_de : asset.asset_class_en}
                            </span>
                            <p className="text-[10px] sm:text-xs text-slate-400 mt-1">{asset.currency}</p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Divider when not showing lookup list */}
            {!showLookupList && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-400">{t('common.or')}</span>
                </div>
              </div>
            )}

            {/* Search by Ticker - hide when showing lookup list */}
            {!showLookupList && (
              <>
                <div className="space-y-1.5 sm:space-y-2">
                  <Label className="text-sm">{t('portfolio.searchByTicker')}</Label>
                  <Input
                    data-testid="search-symbol-input"
                    placeholder={t('portfolio.tickerPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="h-10 sm:h-12 text-sm"
                  />
                </div>
                
                {/* Search by Name */}
                <div className="space-y-1.5 sm:space-y-2">
                  <Label className="text-sm">{t('portfolio.searchByName')}</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="search-name-input"
                      placeholder={t('portfolio.namePlaceholder')}
                      value={nameSearchQuery}
                      onChange={(e) => setNameSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="h-10 sm:h-12 text-sm"
                    />
                    <Button 
                      onClick={handleSearch} 
                      disabled={searching}
                      className="bg-[#0F766E] hover:bg-[#115E59] h-10 sm:h-12 px-4 sm:px-6"
                    >
                      {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="border border-slate-200 rounded-lg max-h-36 sm:max-h-48 overflow-y-auto">
                    {searchResults.map((result) => (
                      <button
                        key={result.symbol}
                        data-testid={`search-result-${result.symbol}`}
                        onClick={() => setSelectedAsset(result)}
                        className={`w-full text-left p-2.5 sm:p-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors ${
                          selectedAsset?.symbol === result.symbol ? 'bg-teal-50' : ''
                        }`}
                      >
                        <div className="flex justify-between items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 text-sm">{result.name}</p>
                            <p className="text-xs text-slate-500">{result.symbol}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {result.current_price && (
                              <p className="font-semibold text-slate-900 text-sm">
                                {result.currency} {fmtNum(result.current_price)}
                              </p>
                            )}
                            <p className="text-[10px] sm:text-xs text-slate-400">{result.type} · {result.region}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Selected Asset */}
            {selectedAsset && (
              <div className="bg-teal-50 p-3 sm:p-4 rounded-lg">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 text-sm">{selectedAsset.name}</p>
                    <p className="text-xs text-slate-500">{selectedAsset.symbol}</p>
                  </div>
                  <div className="text-right flex-shrink-0 mr-2">
                    {selectedAsset.current_price && (
                      <p className="font-semibold text-slate-900 text-sm">
                        {selectedAsset.currency} {fmtNum(selectedAsset.current_price)}
                      </p>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setSelectedAsset(null)}
                    className="h-8 w-8"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Quantity - only show when not in lookup list view */}
            {!showLookupList && (
              <div className="space-y-1.5 sm:space-y-2">
                <Label className="text-sm">{t('portfolio.quantity')}</Label>
                <Input
                  type="number"
                  data-testid="quantity-input"
                  placeholder="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min="0"
                  step="0.01"
                  className="h-10 sm:h-12 text-sm"
                />
              </div>
            )}

            {/* Actions - only show when not in lookup list view */}
            {!showLookupList && (
              <div className="flex justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                <Button variant="ghost" className="text-sm h-9 sm:h-10" onClick={() => {
                  setAddAssetOpen(false);
                  setSelectedAsset(null);
                  setQuantity("");
                  setSearchQuery("");
                  setNameSearchQuery("");
                  setSearchResults([]);
                }}>
                  {t('common.cancel')}
                </Button>
                <Button 
                  data-testid="submit-add-asset"
                  onClick={handleAddAsset}
                  disabled={!selectedAsset || !quantity || submitting}
                  className="bg-[#0F766E] hover:bg-[#115E59] text-sm h-9 sm:h-10"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('portfolio.addAsset')}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* KPI Modal */}
      <Dialog open={kpiModalOpen} onOpenChange={setKpiModalOpen}>
        <DialogContent className="sm:max-w-2xl mx-2 sm:mx-auto max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2 text-sm sm:text-lg">
              <Info className="w-4 h-4 sm:w-5 sm:h-5 text-[#0F766E]" />
              {t('kpi.title')}
            </DialogTitle>
          </DialogHeader>
          
          {kpiLoading ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-[#0F766E]" />
            </div>
          ) : kpiData ? (
            <div className="space-y-3 sm:space-y-6 mt-2 sm:mt-4">
              {/* Header Info */}
              <div className="bg-slate-50 rounded-lg p-2.5 sm:p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-base sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      {kpiData.name}
                    </h3>
                    <p className="text-slate-500 text-xs sm:text-sm">{kpiData.symbol}</p>
                    <div className="flex flex-wrap gap-1.5 sm:gap-4 mt-1.5 text-[10px] sm:text-sm text-slate-500">
                      <span>{kpiData.sector}</span>
                      <span>•</span>
                      <span>{kpiData.industry}</span>
                      <span>•</span>
                      <span>{kpiData.currency}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="ai-view-btn"
                    onClick={handleAiAssetAnalysis}
                    className="text-xs h-8 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 text-purple-700 hover:from-purple-100 hover:to-blue-100 hover:border-purple-300 flex-shrink-0"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    {t('kpi.aiView')}
                  </Button>
                </div>
              </div>

              {/* Editable Quantity + TER */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                <div className="bg-slate-50 rounded-lg p-2.5 sm:p-4">
                  <Label className="text-xs sm:text-sm font-medium text-slate-700">{t('portfolio.quantity')}</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      type="number"
                      min="0.01"
                      step="any"
                      value={kpiQuantity}
                      onChange={(e) => setKpiQuantity(e.target.value)}
                      className="w-32 sm:w-40 h-8 sm:h-9 text-sm"
                      data-testid="kpi-quantity-input"
                    />
                    <Button
                      size="sm"
                      onClick={handleUpdateQuantity}
                      disabled={kpiQuantityUpdating || !kpiQuantity || parseFloat(kpiQuantity) <= 0}
                      className="h-8 sm:h-9 bg-[#0F766E] hover:bg-[#0d6560] text-xs sm:text-sm"
                      data-testid="kpi-quantity-save-btn"
                    >
                      {kpiQuantityUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : t('common.save')}
                    </Button>
                  </div>
                </div>
                {kpiData.ter != null && (
                  <div className="bg-slate-50 rounded-lg p-2.5 sm:p-4" data-testid="kpi-ter">
                    <Label className="text-xs sm:text-sm font-medium text-slate-700">TER</Label>
                    <p className="text-sm sm:text-xl font-semibold text-[#0F766E] mt-1.5" style={{ fontFamily: 'Manrope' }}>
                      {kpiData.ter.toFixed(2)}%
                    </p>
                    <p className="text-[8px] sm:text-xs text-slate-400 mt-0.5">Total Expense Ratio</p>
                  </div>
                )}
              </div>

              {/* Current KPIs */}
              <div>
                <h4 className="text-[10px] sm:text-sm font-semibold text-slate-700 uppercase tracking-wide mb-1.5 sm:mb-3">
                  {t('kpi.financialRatios')}
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-3 gap-1.5 sm:gap-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4 relative">
                    <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                      <KpiInfoButton definitionKey="roe" t={t} />
                    </div>
                    <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight pr-4">{t('kpi.roe')}</p>
                    <p className="text-sm sm:text-xl font-semibold text-[#0F766E]" style={{ fontFamily: 'Manrope' }}>
                      {formatPercent(kpiData.current.return_on_equity)}
                    </p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4 relative">
                    <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                      <KpiInfoButton definitionKey="debtToEquity" t={t} />
                    </div>
                    <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight pr-4">{t('kpi.debtToEquity')}</p>
                    <p className="text-sm sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      {kpiData.current.debt_to_equity ? `${kpiData.current.debt_to_equity.toFixed(1)}%` : t('kpi.noData')}
                    </p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4 relative">
                    <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                      <KpiInfoButton definitionKey="peTrailing" t={t} />
                    </div>
                    <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight pr-4">{t('kpi.peRatio')} (T)</p>
                    <p className="text-sm sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      {formatRatio(kpiData.current.trailing_pe)}
                    </p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4 relative">
                    <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                      <KpiInfoButton definitionKey="peForward" t={t} />
                    </div>
                    <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight pr-4">{t('kpi.peRatio')} (F)</p>
                    <p className="text-sm sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      {formatRatio(kpiData.current.forward_pe)}
                    </p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4 relative">
                    <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                      <KpiInfoButton definitionKey="priceToBook" t={t} />
                    </div>
                    <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight pr-4">{t('kpi.priceToBook')}</p>
                    <p className="text-sm sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      {formatRatio(kpiData.current.price_to_book)}
                    </p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4 relative">
                    <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                      <KpiInfoButton definitionKey="roa" t={t} />
                    </div>
                    <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight pr-4">{t('kpi.roa')}</p>
                    <p className="text-sm sm:text-xl font-semibold text-[#0F766E]" style={{ fontFamily: 'Manrope' }}>
                      {formatPercent(kpiData.current.return_on_assets)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Metrics */}
              <div>
                <h4 className="text-[10px] sm:text-sm font-semibold text-slate-700 uppercase tracking-wide mb-1.5 sm:mb-3">
                  {t('kpi.additionalMetrics')}
                </h4>
                <div className="grid grid-cols-4 sm:grid-cols-4 gap-1 sm:gap-3">
                  <div className="bg-slate-50 rounded-lg p-1.5 sm:p-3">
                    <p className="text-[8px] sm:text-xs text-slate-500 leading-tight">{t('kpi.profitMargin')}</p>
                    <p className="font-semibold text-xs sm:text-base">{formatPercent(kpiData.current.profit_margin)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-1.5 sm:p-3">
                    <p className="text-[8px] sm:text-xs text-slate-500 leading-tight">{t('kpi.operatingMargin')}</p>
                    <p className="font-semibold text-xs sm:text-base">{formatPercent(kpiData.current.operating_margin)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-1.5 sm:p-3">
                    <p className="text-[8px] sm:text-xs text-slate-500 leading-tight">{t('kpi.dividendYield')}</p>
                    <p className="font-semibold text-xs sm:text-base">{formatPercentRaw(kpiData.current.dividend_yield)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-1.5 sm:p-3">
                    <p className="text-[8px] sm:text-xs text-slate-500 leading-tight">{t('kpi.payoutRatio')}</p>
                    <p className="font-semibold text-xs sm:text-base">{formatPercent(kpiData.current.payout_ratio)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-1.5 sm:p-3">
                    <p className="text-[8px] sm:text-xs text-slate-500 leading-tight">{t('kpi.beta')}</p>
                    <p className="font-semibold text-xs sm:text-base">{formatRatio(kpiData.current.beta)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-1.5 sm:p-3">
                    <p className="text-[8px] sm:text-xs text-slate-500 leading-tight">{t('kpi.marketCap')}</p>
                    <p className="font-semibold text-xs sm:text-base">{formatLargeNumber(kpiData.current.market_cap)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-1.5 sm:p-3 col-span-2">
                    <p className="text-[8px] sm:text-xs text-slate-500 leading-tight">{t('kpi.enterpriseValue')}</p>
                    <p className="font-semibold text-xs sm:text-base">{formatLargeNumber(kpiData.current.enterprise_value)}</p>
                  </div>
                </div>
              </div>

              {/* Volatility Metrics */}
              {kpiData.volatility && Object.keys(kpiData.volatility).length > 0 && (
                <div>
                  <h4 className="text-[10px] sm:text-sm font-semibold text-slate-700 uppercase tracking-wide mb-1.5 sm:mb-3">
                    {t('kpi.volatilityRisk')}
                  </h4>
                  <div className="grid grid-cols-3 sm:grid-cols-3 gap-1.5 sm:gap-4">
                    {kpiData.volatility.volatility_30d !== undefined && (
                      <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4">
                        <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight">{t('kpi.volatility30d')}</p>
                        <p className="text-sm sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                          {(kpiData.volatility.volatility_30d * 100).toFixed(1)}%
                        </p>
                        <p className="text-[8px] sm:text-xs text-slate-400 mt-0.5">{t('kpi.annualized')}</p>
                      </div>
                    )}
                    {kpiData.volatility.volatility_90d !== undefined && (
                      <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4">
                        <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight">{t('kpi.volatility90d')}</p>
                        <p className="text-sm sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                          {(kpiData.volatility.volatility_90d * 100).toFixed(1)}%
                        </p>
                        <p className="text-[8px] sm:text-xs text-slate-400 mt-0.5">{t('kpi.annualized')}</p>
                      </div>
                    )}
                    {kpiData.volatility.volatility_1y !== undefined && (
                      <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4">
                        <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight">{t('kpi.volatility1y')}</p>
                        <p className="text-sm sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                          {(kpiData.volatility.volatility_1y * 100).toFixed(1)}%
                        </p>
                        <p className="text-[8px] sm:text-xs text-slate-400 mt-0.5">{t('kpi.annualized')}</p>
                      </div>
                    )}
                    {kpiData.volatility.max_drawdown_1y !== undefined && (
                      <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4">
                        <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight">{t('kpi.maxDrawdown')}</p>
                        <p className={`text-sm sm:text-xl font-semibold ${kpiData.volatility.max_drawdown_1y < -0.2 ? 'text-red-500' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope' }}>
                          {(kpiData.volatility.max_drawdown_1y * 100).toFixed(1)}%
                        </p>
                        <p className="text-[8px] sm:text-xs text-slate-400 mt-0.5">{t('kpi.peakToTrough')}</p>
                      </div>
                    )}
                    {kpiData.volatility.sharpe_ratio !== undefined && (
                      <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4">
                        <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight">{t('kpi.sharpeRatio')}</p>
                        <p className={`text-sm sm:text-xl font-semibold ${kpiData.volatility.sharpe_ratio >= 1 ? 'text-emerald-600' : kpiData.volatility.sharpe_ratio < 0 ? 'text-red-500' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope' }}>
                          {kpiData.volatility.sharpe_ratio.toFixed(2)}
                        </p>
                        <p className="text-[8px] sm:text-xs text-slate-400 mt-0.5">{t('kpi.riskAdjReturn')}</p>
                      </div>
                    )}
                    {kpiData.volatility.avg_daily_range !== undefined && (
                      <div className="bg-white border border-slate-200 rounded-lg p-1.5 sm:p-4">
                        <p className="text-[8px] sm:text-xs text-slate-500 uppercase leading-tight">{t('kpi.avgDailyRange')}</p>
                        <p className="text-sm sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                          {(kpiData.volatility.avg_daily_range * 100).toFixed(2)}%
                        </p>
                        <p className="text-[8px] sm:text-xs text-slate-400 mt-0.5">High-Low/Close</p>
                      </div>
                    )}
                  </div>
                  <p className="text-[9px] sm:text-xs text-slate-400 mt-2 sm:mt-3">
                    {t('kpi.volatilityExplanation')}
                  </p>
                </div>
              )}

              {/* Historical KPIs */}
              {kpiData.historical && kpiData.historical.length > 0 && (
                <div>
                  <h4 className="text-[10px] sm:text-sm font-semibold text-slate-700 uppercase tracking-wide mb-1.5 sm:mb-3">
                    {t('kpi.historicalRatios')}
                  </h4>
                  <div className="overflow-x-auto -mx-3 px-3">
                    <table className="w-full text-[10px] sm:text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-1.5 px-1.5 sm:py-2 sm:px-3 font-medium text-slate-600">{t('kpi.year')}</th>
                          <th className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 font-medium text-slate-600">{t('kpi.roe')}</th>
                          <th className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 font-medium text-slate-600">{t('kpi.roa')}</th>
                          <th className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 font-medium text-slate-600 hidden sm:table-cell">{t('kpi.debtToEquity')}</th>
                          <th className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 font-medium text-slate-600 hidden sm:table-cell">{t('kpi.netIncome')}</th>
                          <th className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 font-medium text-slate-600 hidden sm:table-cell">{t('kpi.equity')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kpiData.historical.map((year) => (
                          <tr key={year.year} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-1.5 px-1.5 sm:py-2 sm:px-3 font-medium">{year.year}</td>
                            <td className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 text-[#0F766E] font-medium">
                              {year.return_on_equity ? `${(year.return_on_equity * 100).toFixed(1)}%` : '-'}
                            </td>
                            <td className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3">
                              {year.return_on_assets ? `${(year.return_on_assets * 100).toFixed(1)}%` : '-'}
                            </td>
                            <td className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 hidden sm:table-cell">
                              {year.debt_to_equity ? `${year.debt_to_equity.toFixed(1)}%` : '-'}
                            </td>
                            <td className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 text-slate-600 hidden sm:table-cell">
                              {year.net_income ? formatLargeNumber(year.net_income) : '-'}
                            </td>
                            <td className="text-right py-1.5 px-1.5 sm:py-2 sm:px-3 text-slate-600 hidden sm:table-cell">
                              {year.equity ? formatLargeNumber(year.equity) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* ROE Chart */}
                  {kpiData.historical.some(h => h.return_on_equity) && (
                    <div className="mt-3 sm:mt-4">
                      <p className="text-[9px] sm:text-xs text-slate-500 mb-1.5 sm:mb-2">{t('kpi.roeTrend')}</p>
                      <div className="h-24 sm:h-32">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={[...kpiData.historical].reverse()}>
                            <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{ fontSize: 9 }} />
                            <YAxis 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 9 }}
                              tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                              width={30}
                            />
                            <Tooltip 
                              formatter={(value) => [`${(value * 100).toFixed(1)}%`, t('kpi.roe')]}
                              contentStyle={{ fontSize: 10, borderRadius: 8 }}
                            />
                            <Bar dataKey="return_on_equity" radius={[4, 4, 0, 0]}>
                              {[...kpiData.historical].reverse().map((entry, index) => (
                                <Cell key={index} fill={entry.return_on_equity >= 0 ? '#0F766E' : '#EF4444'} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <p>{t('kpi.couldNotLoad')} {kpiSymbol}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Asset Price Graph Modal */}
      <Dialog open={assetGraphOpen} onOpenChange={setAssetGraphOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
              <LineChart className="w-5 h-5 text-[#0F766E]" />
              {t('portfolio.priceHistory')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Header Info */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  {assetGraphSymbol}
                </h3>
                <p className="text-slate-600">{assetGraphName}</p>
                <p className="text-xs text-slate-400">
                  {t('portfolio.nativeDisplayed', { native: assetGraphCurrency, display: homeCurrency })}
                </p>
              </div>
              {/* Time Range Selector */}
              <div className="flex gap-1">
                {TIME_RANGES.map((range) => (
                  <button
                    key={range.value}
                    data-testid={`asset-graph-time-range-${range.value}`}
                    onClick={() => handleAssetGraphTimeRangeChange(range.value)}
                    className={`time-range-btn ${assetGraphTimeRange === range.value ? 'active' : ''}`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Chart */}
            {assetGraphLoading ? (
              <div className="h-72 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#0F766E]" />
              </div>
            ) : assetGraphData && assetGraphData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={assetGraphData.map(p => ({ 
                    date: p.date, 
                    value: convertToHomeCurrency(p.close, assetGraphCurrency, false)
                  }))}>
                    <defs>
                      <linearGradient id="colorAssetValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2DD4BF" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#2DD4BF" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        const mm = String(date.getMonth() + 1).padStart(2, '0');
                        const yy = String(date.getFullYear()).slice(-2);
                        return `${mm}-${yy}`;
                      }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => {
                        const symbol = getCurrencySymbol();
                        if (value >= 1000) {
                          return `${symbol === 'CHF' ? 'CHF' : symbol}${(value/1000).toFixed(0)}k`;
                        }
                        return `${symbol === 'CHF' ? 'CHF' : symbol}${value.toFixed(0)}`;
                      }}
                      width={70}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value) => [
                        formatCurrency(value, homeCurrency, false),
                        t('portfolio.price')
                      ]}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { 
                        month: 'long', day: 'numeric', year: 'numeric' 
                      })}
                    />
                    <Area 
                      type="linear" 
                      dataKey="value" 
                      stroke="#0F766E" 
                      strokeWidth={2}
                      fill="url(#colorAssetValue)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-72 flex flex-col items-center justify-center text-slate-400">
                <BarChart3 className="w-12 h-12 mb-3 opacity-50" />
                <p>{t('portfolio.noPriceData')} {assetGraphSymbol}</p>
              </div>
            )}

            {/* Price Stats */}
            {assetGraphData && assetGraphData.length > 0 && (() => {
              // Use portfolio performance data when time range matches for exact consistency
              const perfData = assetGraphTimeRange === timeRange ? performance?.asset_values?.[assetGraphSymbol] : null;
              const nativeStart = perfData?.start_price || assetGraphData[0]?.close || 0;
              const nativeEnd = perfData?.current_price || assetGraphData[assetGraphData.length - 1]?.close || 0;
              const startConv = convertToHomeCurrency(nativeStart, assetGraphCurrency, true);
              const endConv = convertToHomeCurrency(nativeEnd, assetGraphCurrency, false);
              const changePct = startConv > 0 ? ((endConv - startConv) / startConv * 100) : 0;
              const assetAnnualized = calcAnnualizedReturn(startConv, endConv, null, assetGraphTimeRange);
              return (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 pt-4 border-t border-slate-100">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{t('portfolio.startPrice')} ({homeCurrency})</p>
                  <p className="font-semibold">
                    {formatCurrency(nativeStart, assetGraphCurrency, true)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{t('portfolio.currentPrice')} ({homeCurrency})</p>
                  <p className="font-semibold">
                    {formatCurrency(nativeEnd, assetGraphCurrency, false)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{t('portfolio.change')}</p>
                  <p className={`font-semibold ${changePct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{t('portfolio.annualizedReturn')}</p>
                  <p className={`font-semibold ${assetAnnualized !== null && assetAnnualized >= 0 ? 'text-emerald-600' : 'text-red-500'}`} data-testid="asset-annualized-return">
                    {assetAnnualized !== null ? `${assetAnnualized >= 0 ? '+' : ''}${assetAnnualized.toFixed(2)}% p.a.` : '-'}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{t('portfolio.periodHigh')} ({homeCurrency})</p>
                  <p className="font-semibold">
                    {formatCurrency(Math.max(...assetGraphData.map(p => p.high || p.close)), assetGraphCurrency, false)}
                  </p>
                </div>
              </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Asset Analysis Dialog */}
      <Dialog open={aiAssetOpen} onOpenChange={setAiAssetOpen}>
        <DialogContent className="w-[95vw] max-w-2xl mx-auto max-h-[85vh] overflow-hidden flex flex-col p-3 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2 text-sm sm:text-lg">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              {t('kpi.aiViewTitle')}
            </DialogTitle>
            {aiAssetResult && (
              <DialogDescription className="text-xs sm:text-sm">
                {aiAssetResult.symbol} - {aiAssetResult.name} ({aiAssetResult.asset_type})
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto mt-2 sm:mt-4">
            {aiAssetLoading ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16">
                <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 animate-spin text-purple-600 mb-4" />
                <p className="text-slate-500 text-sm">{t('kpi.aiViewAnalyzing')}</p>
                <p className="text-slate-400 text-xs mt-1">This may take 15-30 seconds...</p>
              </div>
            ) : aiAssetResult ? (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-3 sm:p-6">
                <div className="prose prose-sm prose-slate max-w-none text-slate-700 
                  prose-headings:text-purple-900 prose-headings:font-semibold 
                  prose-h3:text-xs prose-h3:sm:text-sm prose-h3:mt-3 prose-h3:mb-1.5
                  prose-p:text-[11px] prose-p:sm:text-sm prose-p:leading-relaxed prose-p:my-1.5
                  prose-strong:text-slate-800 prose-strong:font-semibold
                  prose-ul:text-[11px] prose-ul:sm:text-sm prose-ul:my-1 prose-ul:pl-4
                  prose-li:my-0.5">
                  <ReactMarkdown>
                    {aiAssetResult.analysis}
                  </ReactMarkdown>
                </div>
                <p className="text-[10px] sm:text-xs text-slate-400 mt-4 text-right">
                  {new Date(aiAssetResult.generated_at).toLocaleString()}
                </p>
              </div>
            ) : null}
          </div>

          <div className="flex justify-end pt-3 sm:pt-4 border-t border-slate-100 mt-3 sm:mt-4">
            <Button variant="outline" onClick={() => setAiAssetOpen(false)} className="text-xs sm:text-sm h-8 sm:h-10">
              {t('analysis.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioDetail;
